/**
 * Tearing this app down on the way out to Vue 3.
 *
 * `src/main.js` registers a Workbox service worker in production. Its precache manifest is
 * built with `directoryIndex: index.html`, so the worker answers a navigation to `/` out of
 * CacheStorage — this build's index.html — without ever reaching the network. That is fatal
 * to the crossover: the root `middleware.js` only sees requests that actually leave the
 * browser, so after opting in, the homepage would keep booting the legacy shell until the
 * worker happened to update. Retiring the worker and its caches before we navigate is what
 * makes the opt-in take effect on the very next page load.
 *
 * Both handles are injected so this unit-tests without a browser, and so a browser that has
 * neither (older Safari, an insecure origin, a hardened profile) is simply a no-op.
 */

/** Every promise here is caught: a teardown failure must not stop the caller from navigating. */
async function quietly (work) {
  try {
    await work()
  } catch (e) {
    // Nothing to do — the user is leaving this app either way, and a stale worker degrades
    // to "the switch needs a second load", not to a broken page.
  }
}

/**
 * True for this app's own Workbox worker (`registerServiceWorker.js` registers
 * `service-worker.js`). Vue 3 shares the origin, and its `/alerts` page registers a push worker
 * of its own; unregistering that would silently drop the phone's notification subscription on
 * every switch, so only this app's worker is retired.
 */
function isLegacyRegistration (registration) {
  const worker = registration.active || registration.waiting || registration.installing
  return !!worker && new URL(worker.scriptURL).pathname.endsWith('/service-worker.js')
}

/**
 * Unregister this app's service worker and drop every CacheStorage bucket.
 * Always resolves (undefined), even when a step throws or the browser lacks the APIs.
 *
 * Only a registration with an ACTIVE worker is waited on: that is the only kind that can answer
 * the navigation from cache. On a browser's first visit the worker is still installing (it
 * precaches the whole build), and service-worker jobs for a scope run one at a time, so
 * `unregister()` does not settle until that install finishes. Awaiting it held a fresh sign-in
 * on a half-switched page for as long as the precache took. Its unregister is still queued and
 * runs after the install, with nothing waiting on it.
 */
export async function unregisterLegacyServiceWorker ({ serviceWorker = navigator.serviceWorker, caches = window.caches } = {}) {
  if (serviceWorker) {
    await quietly(async () => {
      const registrations = await serviceWorker.getRegistrations()
      const unregistering = registrations.filter(isLegacyRegistration).map(r => ({ active: !!r.active, done: quietly(() => r.unregister()) }))
      await Promise.all(unregistering.filter(u => u.active).map(u => u.done))
    })
  }

  if (caches) {
    await quietly(async () => {
      const keys = await caches.keys()
      await Promise.all(keys.map(key => quietly(() => caches.delete(key))))
    })
  }
}
